<div class="mod-page">
  <app-project-nav></app-project-nav>
  <app-page-header
    size="compact"
    [title]="'modules.pageTitle' | translate"
    [subtitle]="'modules.pageSubtitle' | translate"
  >
    <ng-container right>
      <app-stat-group
        class="mod-header-stats"
        role="group"
        [items]="statItems()"
        [attr.aria-label]="statsAriaLabel()"
      />
      @if (!isReadOnly()) {
        <button type="button" class="mod-create-btn" (click)="onCreate()">
          <mat-icon fontSet="material-icons-outlined" aria-hidden="true">add</mat-icon>
          {{ 'modules.addButton' | translate }}
        </button>
      }
    </ng-container>
  </app-page-header>

  <div class="mod-toolbar">
    <app-search-bar
      class="mod-search"
      [placeholder]="'modules.searchPlaceholder' | translate"
      [value]="searchTerm()"
      (valueChange)="onSearchChange($event)"
    />
    <app-module-filter-bar
      [statusOptions]="statusOptions()"
      [authorOptions]="authorOptions()"
      [selectedStatus]="selectedStatus()"
      [selectedAuthor]="selectedAuthor()"
      [tagOptions]="tagOptions()"
      [selectedTags]="tagFilter()"
      [hasActiveFilters]="hasActiveFilters()"
      [savedViews]="savedViewsOptions()"
      [activeSavedView]="activeSavedViewName()"
      [projectId]="projectId"
      (filterChange)="onFilterChange($event)"
      (tagChange)="onTagFilterChange($event)"
      (resetFilters)="onResetFilters()"
      (savedViewSelected)="onSavedViewSelected($event)"
      (savedViewSaved)="onSavedViewSaved($event)"
      (savedViewDeleted)="onSavedViewDeleted($event)"
      (savedViewDeleteFailed)="onSavedViewDeleteFailed()"
    />
    <span class="mod-result-count">
      {{ resultCountKey() | translate: { count: total(), total: unfilteredTotal() } }}
    </span>
  </div>

  @if (deleteError()) {
    <p class="mod-hint mod-hint--error" role="alert" aria-live="polite">{{ deleteErrorKey() | translate }}</p>
  }

  @if (savedViewsError()) {
    <p class="mod-hint mod-hint--error" role="alert" aria-live="polite">{{ savedViewsError() }}</p>
  }

  @if (loading()) {
    <div class="mod-loading">
      <mat-spinner diameter="32" />
    </div>
  } @else if (loadError()) {
    <p class="mod-hint mod-hint--error" role="alert">{{ 'modules.loadError' | translate }}</p>
  } @else if (rows().length === 0) {
    <p class="mod-hint">{{ 'modules.empty' | translate }}</p>
  } @else {
    <div appListFocusScroll class="mod-card" role="table" [attr.aria-label]="'modules.tableLabel' | translate">
      <div class="mod-table-header" role="row">
        @for (column of sortColumns; track column.col) {
          @let aria = sortAria(column.col);
          <div class="mod-col" [class]="column.css" role="columnheader" [attr.aria-sort]="aria">
            <button
              type="button"
              class="mod-sort-btn"
              [class.mod-sort-btn--active]="aria !== 'none'"
              (click)="sortBy(column.col)"
            >
              {{ column.labelKey | translate }}
              <span class="mod-sort-btn__arrows" aria-hidden="true">
                <span class="mod-sort-btn__arrow" [class.mod-sort-btn__arrow--on]="aria === 'ascending'">▲</span>
                <span class="mod-sort-btn__arrow" [class.mod-sort-btn__arrow--on]="aria === 'descending'">▼</span>
              </span>
            </button>
          </div>
        }
        <div class="mod-col mod-col--actions" role="columnheader">
          {{ 'modules.table.actions' | translate }}
        </div>
      </div>

      @if (!hasResults()) {
        <div class="mod-empty" role="row">
          <p class="mod-hint mod-hint--filtered" role="cell">{{ 'modules.emptyFiltered' | translate }}</p>
        </div>
      }

      @for (row of pagedRows(); track row.id; let last = $last) {
        <div
          class="mod-row"
          [class.mod-row--last]="last"
          role="row"
          tabindex="0"
          [attr.aria-label]="
            row.recursive ? ('modules.recursive.rowAria' | translate: { title: row.title }) : row.title
          "
          (click)="onEdit(row)"
          (keydown.enter)="onRowKeydown($event, row)"
          (keydown.space)="onRowKeydown($event, row)"
        >
          <div class="mod-col mod-col--id" role="cell">
            <app-code-id-chip [id]="row.displayId" />
          </div>
          <div class="mod-col mod-col--title" role="cell">
            <span class="mod-title-line">
              <span class="mod-title">{{ row.title }}</span>
              @if (row.recursive) {
                <app-recursive-badge />
              }
            </span>
            @if (row.description) {
              <span class="mod-desc">{{ row.description }}</span>
            }
          </div>
          <div class="mod-col mod-col--steps" role="cell">
            <span class="mod-count" [attr.aria-label]="'modules.stepsAria' | translate: { count: row.stepCount }">
              <span class="mod-count__value">{{ 'modules.stepsValue' | translate: { count: row.stepCount } }}</span>
              &ngsp;
              <span class="mod-count__unit">{{ 'modules.stepsUnit' | translate }}</span>
            </span>
          </div>
          <div class="mod-col mod-col--usage" role="cell">
            <div class="mod-usage-lines">
              <span
                class="mod-usage"
                data-testid="modules-usage-cases"
                [class.mod-usage--zero]="row.usageCount === 0"
                [attr.title]="usageTitleKey('cases', row.usageCount) | translate: { count: row.usageCount }"
              >
                <mat-icon class="mod-usage__icon" fontSet="material-icons-outlined" aria-hidden="true">folder</mat-icon>
                <strong class="mod-usage__count">{{ row.usageCount }}</strong>
                {{ usageUnitKey('cases', row.usageCount) | translate }}
              </span>
              <span
                class="mod-usage"
                data-testid="modules-usage-modules"
                [class.mod-usage--zero]="row.moduleUsageCount === 0"
                [attr.title]="
                  usageTitleKey('modules', row.moduleUsageCount) | translate: { count: row.moduleUsageCount }
                "
              >
                <mat-icon class="mod-usage__icon" fontSet="material-icons-outlined" aria-hidden="true">
                  content_copy
                </mat-icon>
                <strong class="mod-usage__count">{{ row.moduleUsageCount }}</strong>
                {{ usageUnitKey('modules', row.moduleUsageCount) | translate }}
              </span>
            </div>
          </div>
          <div class="mod-col mod-col--status" role="cell">
            <app-lifecycle-pill [kind]="row.statusKind" [label]="row.statusLabel" />
          </div>
          <div class="mod-col mod-col--author" role="cell">
            @if (row.authorName) {
              <app-avatar [name]="row.authorName" [size]="28" aria-hidden="true" />
              <span class="mod-author__name">{{ row.authorName }}</span>
            } @else {
              <span class="mod-none" [attr.aria-label]="'modules.table.noAuthor' | translate">—</span>
            }
          </div>
          <div class="mod-col mod-col--actions" role="cell">
            @if (!isReadOnly()) {
              <button
                type="button"
                class="mod-action-btn mod-action-btn--danger"
                [attr.title]="'modules.delete' | translate"
                [attr.aria-label]="'modules.delete' | translate"
                (click)="$event.stopPropagation(); onDeleteRequest(row)"
              >
                <mat-icon fontSet="material-icons-outlined" aria-hidden="true">delete</mat-icon>
              </button>
            }
          </div>
        </div>
      }
    </div>

    @if (hasResults()) {
      <div class="mod-footer">
        <span class="mod-footer__range">
          {{ 'modules.footer.rangeLabel' | translate: { start: rangeStart(), end: rangeEnd(), total: total() } }}
        </span>
        <div class="mod-footer__pager">
          <select
            id="mod-page-size"
            class="mod-footer__select"
            (change)="onPageSizeChange(+pageSizeSelect.value)"
            #pageSizeSelect
            [attr.aria-label]="'modules.footer.rowsPerPageAriaLabel' | translate"
          >
            @for (size of pageSizeOptions; track size) {
              <option [value]="size" [selected]="pageSize() === size">{{ size }}</option>
            }
          </select>
          <div class="mod-footer__nav">
            <button
              type="button"
              class="mod-action-btn"
              [disabled]="pageIndex() === 0"
              [attr.aria-label]="'modules.footer.previousPage' | translate"
              (click)="onPageChange(pageIndex() - 1)"
            >
              <mat-icon aria-hidden="true">chevron_left</mat-icon>
            </button>
            <button
              type="button"
              class="mod-action-btn"
              [disabled]="pageIndex() >= pageCount() - 1"
              [attr.aria-label]="'modules.footer.nextPage' | translate"
              (click)="onPageChange(pageIndex() + 1)"
            >
              <mat-icon aria-hidden="true">chevron_right</mat-icon>
            </button>
          </div>
        </div>
      </div>
    }
  }
</div>

<app-confirm-dialog
  class="mod-delete-dialog"
  [visible]="showDeleteModal()"
  [title]="'modules.deleteTitle' | translate"
  [message]="deleteMessage()"
  [confirmLabel]="'modules.deleteConfirm' | translate"
  [cancelLabel]="'modules.deleteCancel' | translate"
  [busy]="deleting()"
  [confirmDisabled]="deleteConfirmDisabled()"
  (confirmed)="onConfirmDelete()"
  (cancelled)="onCancelDelete()"
>
  @if (deleteBlocked()) {
    <div class="mod-delete-usage">
      <div class="mod-delete-usage__label" id="mod-delete-usage-label">{{ 'modules.deleteUsedBy' | translate }}</div>
      <ul class="mod-delete-usage__list" role="list" aria-labelledby="mod-delete-usage-label">
        @for (usage of deleteUsage(); track usage.id) {
          <li class="mod-delete-usage__item">
            @if (usage.kind === 'MODULE') {
              <mat-icon class="mod-delete-usage__kind" aria-hidden="true">extension</mat-icon>
            }
            <code class="mod-delete-usage__key">{{ usage.key }}</code>
            <span class="mod-delete-usage__title">{{ usage.title }}</span>
          </li>
        }
      </ul>
    </div>
  }
</app-confirm-dialog>
